import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";
import { Camera, CameraOff, Search } from "lucide-react";
import { PageTitle } from "@/components/SettingsPanel";
import { AttendancePanel } from "@/components/AttendancePanel";
import { TodayList } from "@/components/TodayList";
import { Input } from "@/components/ui-kit";
import { api, errorText } from "@/lib/api";
import { usePrefs } from "@/contexts/PrefsContext";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import type { Booking, User } from "@/types";

const READER_ID = "wn-qr-reader";

export default function VenueCheckin() {
  const { t } = usePrefs();
  const me = useAuth().user as User;
  const scanner = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [code, setCode] = useState("");
  const [result, setResult] = useState<Booking | null>(null);
  const [busy, setBusy] = useState(false);
  const [rk, setRk] = useState(0);

  const stop = async () => {
    try {
      if (scanner.current?.isScanning) await scanner.current.stop();
    } catch {}
    setScanning(false);
  };

  useEffect(() => () => void stop(), []);

  const lookup = async (raw: string) => {
    if (!raw.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.get("/venue/bookings/lookup", { params: { code: raw.trim() } });
      setResult(data);
      if (data.checked_in && data.attended_count >= data.guests) toast(t("checkin.already"));
    } catch (e) {
      toast.error(errorText(e, t));
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    setResult(null);
    try {
      scanner.current = scanner.current || new Html5Qrcode(READER_ID);
      await scanner.current.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (text) => {
          stop();
          lookup(text);
        },
        () => {}
      );
      setScanning(true);
    } catch {
      toast.error(t("checkin.camError"));
      setScanning(false);
    }
  };

  if (me.venue_role === "finance") return <Navigate to="/venue" replace />;
  return (
    <div data-testid="venue-checkin-page" className="max-w-xl space-y-5">
      <PageTitle title={t("checkin.title")} sub={t("checkin.sub")} />
      <div className="rounded-3xl border bg-card p-4 space-y-4">
        <div className={`relative overflow-hidden rounded-2xl bg-black ${scanning ? "" : "hidden"}`}>
          <div id={READER_ID} data-testid="qr-reader" className="w-full" />
        </div>
        {!scanning && (
          <div className="aspect-[4/3] rounded-2xl bg-gradient-to-br from-primary/15 to-secondary/20 grid place-items-center">
            <Camera className="h-16 w-16 text-primary/60" />
          </div>
        )}
        <button
          data-testid={scanning ? "checkin-stop-camera-button" : "checkin-start-camera-button"}
          onClick={scanning ? stop : start}
          className={`w-full h-12 rounded-full font-bold inline-flex items-center justify-center gap-2 transition-[background-color] ${scanning ? "border-2 hover:bg-accent" : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
        >
          {scanning ? <CameraOff className="h-5 w-5" /> : <Camera className="h-5 w-5" />}
          {scanning ? t("checkin.stopCam") : t("checkin.startCam")}
        </button>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            lookup(code);
          }}
          className="space-y-2"
        >
          <p className="text-sm font-semibold text-muted-foreground">{t("checkin.manual")}</p>
          <div className="flex gap-2">
            <Input dir="ltr" value={code} onChange={(e: any) => setCode(e.target.value.toUpperCase())} placeholder="WN-XXXXXX" data-testid="checkin-code-input" className="h-12 rounded-full font-mono tracking-widest" />
            <button disabled={busy || !code.trim()} data-testid="checkin-find-button" className="h-12 px-5 rounded-full bg-foreground text-background font-bold inline-flex items-center gap-1.5 disabled:opacity-50">
              <Search className="h-4 w-4" />
              {t("checkin.find")}
            </button>
          </div>
        </form>
      </div>
      {result && (
        <AttendancePanel
          key={result.booking_id}
          b={result}
          onSaved={(b) => { setResult(b); setRk((n) => n + 1); }}
          onReset={() => {
            setResult(null);
            setCode("");
          }}
        />
      )}
      <TodayList refreshKey={rk} onPick={(b) => { setResult(b); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
    </div>
  );
}
